import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { PlusIcon, SettingsIcon, Trash2Icon } from "@lucide/icons";

import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Loader } from "simplydo/react/ui/loader";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  type ModalProps,
} from "simplydo/react/ui/modal";

import api from "api";
import util from "utils/utils";

import RoleTable from "components/lib/Roles/RoleTable";

import { PermissionGroup } from "./RoleEditor";
import type { IRole, RolesGetPathParams } from "./types";

type RoleAssignerProps = {
  forType: "organisation" | "group" | "global" | "ideaBusinessProfile";
  forId: string;
  permissionGroups: PermissionGroup[];
  forUsers: string[];
  modalProps?: ModalProps;
  onAssign?: (userIds: string[], role: IRole) => void;
  onUnassign?: (userIds: string[], role: IRole) => void;
};

const RoleAssigner = ({
  forType,
  forId,
  forUsers,
  permissionGroups,
  modalProps = {},
  onAssign,
  onUnassign,
}: RoleAssignerProps) => {
  const { t } = useTranslation();

  const [roles, setRoles] = useState<IRole[]>([]);
  const [userRoles, setUserRoles] = useState<IRole[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [assigningRole, setAssigningRole] = useState<string>("");
  const usingForId = forType === "global" ? "global" : forId;

  const [roleNameSearch, setRoleNameSearch] = useState("");
  const [rolePermissionSearch, setRolePermissionSearch] = useState("");
  const navigate = useNavigate();

  const filteredRoles = useMemo(() => {
    const nameSearch = roleNameSearch.toLowerCase();
    const permissionSearch = rolePermissionSearch.toLowerCase();

    if (!nameSearch && !permissionSearch) {
      return roles;
    }

    return roles.filter((role) => {
      const nameMatch = role.name.toLowerCase().includes(nameSearch);
      if (nameSearch && !nameMatch) {
        return false;
      }
      if (!permissionSearch) {
        return true;
      }
      for (const permission of role.permissions) {
        if (permission.toLowerCase().includes(permissionSearch)) {
          return nameMatch;
        }

        const option = permissionGroups
          .flatMap((group) =>
            group.permissions.flatMap((category) => category.permissions),
          )
          .find((perm) => perm.key === permission);
        if (!option) {
          return false;
        }
        if (t(option.title).toLowerCase().includes(permissionSearch)) {
          return nameMatch;
        }
      }

      return false;
    });
  }, [roleNameSearch, permissionGroups, rolePermissionSearch, roles, t]);

  const usingSingleUser = forUsers.length === 1;

  const getUserRoles = useCallback(() => {
    if (!forUsers.length) return;
    setLoading(true);
    /*
      If assigning to a single user, we can get their current roles and be able to add/remove
      For multiple users we don't know what roles they currently have - so roles can only be added
    */
    if (usingSingleUser) {
      const userId = forUsers[0];
      if (!userId) return;
      api.roles.getForUser(
        forType,
        usingForId,
        userId,
        (data) => {
          setRoles(data.allRoles);
          setUserRoles(data.roles);
          setLoading(false);
        },
        (err) => {
          toast.error(err.message);
          setLoading(false);
        },
      );
    } else {
      const rolePathParams: RolesGetPathParams = {
        for_type: forType as RolesGetPathParams["for_type"],
        for_id: usingForId,
      };
      api.roles.get(
        forType,
        usingForId,
        rolePathParams,
        (data) => {
          setRoles(data.roles);
          setLoading(false);
        },
        (err) => {
          toast.error(err.message);
          setLoading(false);
        },
      );
    }
  }, [forUsers, usingSingleUser, forType, usingForId]);

  const assignRoleToUser = useCallback(
    (roleId: string) => {
      if (!forUsers.length) return;
      setAssigningRole(roleId);
      const usingRole = roles.find((role) => role._id === roleId);
      if (!usingRole) return;
      api.roles.assignRole(
        forType,
        usingForId,
        roleId,
        forUsers,
        () => {
          setAssigningRole("");
          usingRole.userCount = (usingRole.userCount ?? 0) + 1;
          setUserRoles((prevRoles) => [...prevRoles, usingRole]);
          toast.success(
            `Role ${roles.find((role) => role._id === roleId)?.name} assigned to user`,
          );
          onAssign?.(forUsers, usingRole);
        },
        (err) => {
          toast.error(err.message);
          setAssigningRole("");
        },
      );
    },
    [forType, forUsers, roles, usingForId, onAssign],
  );

  const unassignRoleFromUser = useCallback(
    (roleId: string) => {
      if (!forUsers.length) return;
      setAssigningRole(roleId);
      const usingRole = roles.find((role) => role._id === roleId);
      if (!usingRole) return;
      api.roles.unassignRole(
        forType,
        usingForId,
        roleId,
        forUsers,
        () => {
          setAssigningRole("");
          usingRole.userCount = (usingRole.userCount ?? 0) - 1;
          setUserRoles((prevRoles) =>
            prevRoles.filter((role) => role._id !== roleId),
          );
          toast.success(`Role ${usingRole.name} unassigned from user`);
          onUnassign?.(forUsers, usingRole);
        },
        (err) => {
          toast.error(err.message);
          setAssigningRole("");
        },
      );
    },
    [forType, forUsers, roles, usingForId, onUnassign],
  );

  useEffect(() => {
    getUserRoles();
  }, [getUserRoles]);

  const openRolesSettings = useMemo(() => {
    if (forType === "organisation") {
      return () => navigate({ search: `?tab=${3}` });
    }
  }, [navigate, forType]);

  return (
    <Modal {...modalProps} open={!!forUsers.length}>
      <ModalHeader>Assign roles to user</ModalHeader>
      <ModalContent
        scrolling
        style={{ display: "flex", flexDirection: "column" }}
      >
        <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
          <Input
            placeholder="Search by name..."
            value={roleNameSearch}
            onChange={(e) => setRoleNameSearch(e.target.value)}
          />
          <Input
            placeholder="Search by permission..."
            value={rolePermissionSearch}
            onChange={(e) => setRolePermissionSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <Loader />
        ) : (
          <RoleTable
            roles={filteredRoles}
            permissionGroups={permissionGroups}
            getRoleActions={(role) => {
              const userHasRole = usingSingleUser
                ? userRoles.map((userRole) => userRole._id).includes(role._id)
                : false;
              if (role.isDefaultRole) {
                return null;
              }

              return (
                <Button
                  loading={assigningRole === role._id}
                  onClick={() => {
                    if (userHasRole) {
                      util
                        .confirm(
                          "Removing role",
                          `Are you sure you want to remove ${role.name} from ${util.pluralise(forUsers.length, "this user", "these users", false)}?`,
                        )
                        .then(() => {
                          unassignRoleFromUser(role._id);
                        })
                        .catch(() => {});
                    } else {
                      util
                        .confirm(
                          "Assign role",
                          `Are you sure you want to add ${util.pluralise(forUsers.length, "this user", "these users", false)} to ${role.name}?`,
                        )
                        .then(() => {
                          assignRoleToUser(role._id);
                        })
                        .catch(() => {});
                    }
                  }}
                  color={!userHasRole ? "primary" : undefined}
                  variant={userHasRole ? "outline" : "regular"}
                >
                  <Icon icon={userHasRole ? Trash2Icon : PlusIcon} />
                  {userHasRole ? "Remove" : "Assign"}
                </Button>
              );
            }}
          />
        )}
      </ModalContent>
      <ModalActions>
        {openRolesSettings ? (
          <Button onClick={() => openRolesSettings()} variant="outline">
            <Icon icon={SettingsIcon} />
            Manage roles
          </Button>
        ) : null}
        <Button onClick={(e) => modalProps.onClose?.(e, { open: false })}>
          Done
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default RoleAssigner;
